<template>
    <component :is="componentEntity" :tagsList="tagsList" :category="category" :pageNum="pageNum" />
</template>

<script setup lang="ts" name="TagsIndex">
    // 标签页处理，这是一个比较复杂的处理方式，由于有多种情况出现在这种页面中，比如
    // （1）localhost/tags/child-resistant ， （2）localhost/tags/child-resistant_papal-material ，（3）localhost/tags/child-resistant?page=1 ，  （4）localhost/box/tags/child-resistant ， （5）localhost/box/tags/child-resistant?page=1 ，它们分别是（1）标签页，（2）多标签页，（3）标签页分页，（4）分类标签页，（5）分类标签页分页。所以需要把这几种情况都分出来，并进行处理。

    // 测试地址： http://localhost:3033/tags/pre-roll
    //            http://localhost:3033/tags/pre-roll_child-registant
    //            http://localhost:3033/tags/pre-roll?page=2
    //            http://localhost:3033/packaging/box/tags/pre-roll
    //            http://localhost:3033/packaging/box/tags/pre-roll?page=2

    import { ref, shallowRef, watch, computed } from 'vue'
    import type { Category ,Tags} from '~/types';
    import cmsUtils  from '~/utils/cmsUtils';
    const componentEntity = shallowRef()

    // 获取 tags 数据
    const route = useRoute()

    let category = ref<Category | null>(null);
    let tagsList:Tags[] = [];
    let baseUrl = route.path.replace(".html", "");
    let tagsUrl = baseUrl.split('/tags/')[1]
    let categoryUrl = baseUrl.split('/tags/')[0]

    const cmsInfo = await cmsUtils.getCmsInfo();

    //  这里先处理 category 的逻辑
    if (!cmsUtils.isEmpty(categoryUrl)) {
      // 这里的category非空，这里需要记录category的值
      categoryUrl = categoryUrl!.substring(categoryUrl!.lastIndexOf('/') + 1, categoryUrl!.length);
      category.value = cmsUtils.getCategoryByUrl(categoryUrl,cmsInfo)
    }

    // 使用 computed 来响应式地获取页码 pageNum
    const pageNum = computed(() => {
        const pageQuery = route.query.page
        if (pageQuery) {
            // 确认 pageNum 是有效数字，保证 URL 的正确性
            const pageNumValue = parseInt(pageQuery as string)
            if (!/^\d+$/.test(pageQuery as string) || pageNumValue <= 0 || isNaN(pageNumValue)) {
                return 1
            }
            return pageNumValue
        }
        return 1
    })

    // 这里获得tagsList，就是通过 pre-roll_child-registant 或者 child-registant 这样的url，获得标签列表。
    // tagsList 中可能一个，或者多个tags的结构。
    tagsList =  cmsUtils.getTagsListByUrl(tagsUrl!,cmsInfo)
    if (tagsList.length == 0) {
        // 标签不存在，说明后台还没有设置这个标签，跳转到404页面
        if (import.meta.client) {
            showError({ statusCode: 404, statusMessage: 'url is not found ', message:'tags is not found, please check the url!' ,fatal: true })
        } else {
            throw createError({ statusCode: 404, statusMessage: 'url is not found ', message:'tags is not found, please check the url!' ,fatal: true })
        }
    }

    let resolveComp= null;
    
    if(cmsUtils.isEmpty(tagsList[0]!.vueTemplateName)){
        // 情况1: 标签没有设置vueTemplateName，直接使用TagsDefault
        componentEntity.value  = resolveComponent('TagsDefault') 
    }

    if(componentEntity.value == null){
        //情况2：如果标签有设置vueTemplateName，但是nuxt没有找到对应的标签模板文件使用默认模板。
        resolveComp = resolveComponent(tagsList[0]!.vueTemplateName)
        if(typeof resolveComp === 'string'){
            // 数据库设置了tags对应的模板，但是nuxt这里找不到这个模板文件，那么默认就给他设置为TagsDefault的模板。
            componentEntity.value  = resolveComponent('TagsDefault') 
        }
    }

    if(componentEntity.value == null ){
        // 情况3：即使是找到了对应的组件，但是url中没有category的前缀造成category.value 不存在，同时vueTemplateName是Category开头，那么默认还是跳转到TagsDefault，这是为了防止url中没有category的前缀造成category.value 不存在，同时vueTemplateName是Category开头，那么默认还是跳转到TagsDefault
        // tags/xxx.html,前面没有分类的参数，那么就直接使用DefaultTags
        // 没有分类的Url，是不允许加载分类的模板。
        if(category.value == null && tagsList[0]!.vueTemplateName.startsWith('Category')){
            componentEntity.value  =  resolveComponent('TagsDefault') 
        }
    }

    //情况4：正常的加载。
    if(componentEntity.value == null){
        componentEntity.value  = resolveComp
    }


    // 这里统一进行seo 处理，判断 tagsList 的长度，如果有多个标签，则使用 concat 拼接 title、description、keywords
    let canonicalUrl = (cmsInfo.website.https?'https://':'http://') + cmsInfo.website.domain + route.path;
    let seoTitle = ''
    let seoDescription = ''
    let seoKeywords = ''


    const replaceIgnoreCase = (source: string, search: string, replacement: string) => {
        if (!source || !search) return source || ''
        const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        return source.replace(new RegExp(escaped, 'gi'), replacement)
    } 

    if(category.value !=null){
        // 如果访问的有目录，那么目录就是使用的category 的title 和 keywords 和description，知识把里面对应的category.value.name 替换为 replacesTagsName + ' category.value.name'
        let replacesTagsName = tagsList.map(item => item.name).filter(Boolean).join(' ')
        seoTitle = category.value.title ? category.value.title : ''
        seoKeywords = category.value.keywords ? category.value.keywords : ''
        seoDescription = category.value.description ? category.value.description : ''
        const replacement = `${replacesTagsName} ${category.value.name}`
        seoTitle = replaceIgnoreCase(seoTitle, category.value.name, replacement)
        seoKeywords = replaceIgnoreCase(seoKeywords, category.value.name, replacement)
        seoDescription = replaceIgnoreCase(seoDescription, category.value.name, replacement)
    }else{
        // 如果没有的话，使用tags 自己的title 和 keywords 和 description
        seoTitle = tagsList.map(tag => tag.title).filter(Boolean).join(' | ')
        seoDescription = tagsList.map(tag => tag.description).filter(Boolean).join(' ')
        seoKeywords = tagsList.map(tag => tag.keywords).filter(Boolean).join(', ')

        // 如果拼接后为空，使用第一个标签的值
        if (!seoTitle && tagsList[0]!.title) {
            seoTitle = tagsList[0]!.title
        }
        if (!seoDescription && tagsList[0]!.description) {
            seoDescription = tagsList[0]!.description
        }
        if (!seoKeywords && tagsList[0]!.keywords) {
            seoKeywords = tagsList[0]!.keywords
        }
    }
 



    useHead({
        title:  seoTitle,
        meta: [
            { name: 'title', content: seoTitle },
            { name: 'keywords', content: seoKeywords },
            { name: 'description', content: seoDescription },
            // Open Graph
            { property: 'og:title', content: seoTitle },
            { property: 'og:description', content: seoDescription },
            { property: 'og:url', content: (cmsInfo.website.https?'https://':'http://') + cmsInfo.website.domain + route.path.replace('.html','') },
            { property: 'og:image', content: category.value?.imageUrl || "https://images.leaflypackaging.com/profile/upload/2025/11/24/1_20251124233036A077.webp" },
            { property: 'og:site_name', content: cmsInfo.website.title || 'The Leafly Packaging Company' },
            { property: 'og:type', content: 'website' },
            { property: 'og:locale', content: 'en_US' },
            // Twitter Card
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:title', content: seoTitle },
            { name: 'twitter:description', content: seoDescription },
        ],
        link: [
            { rel: 'canonical', href: (cmsInfo.website.https?'https://':'http://') + cmsInfo.website.domain + route.path.replace('.html','') }
        ]
    })
</script>

<style scoped lang="scss"></style>
